import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxMaterialTimepickerComponent } from 'ngx-material-timepicker';

export interface LocalTimePickerDialogData {
  title: string;
  description?: string;
  date?: Date | string | null;
  timezone: string;
}

@Component({
  selector: 'app-local-time-picker-dialog',
  templateUrl: './local-time-picker-dialog.component.html',
  styleUrls: ['./local-time-picker-dialog.component.scss'],
})
export class LocalTimePickerDialogComponent implements OnInit {
  @ViewChild('timepicker') timepicker: NgxMaterialTimepickerComponent;

  localTime24 = '';
  localDateLabel = '';
  utcPreview = '';
  validationMessage = '';

  get isApplyDisabled(): boolean {
    return !this.data.date || !this.getNormalizedTime(this.localTime24);
  }

  constructor(
    public dialogRef: MatDialogRef<LocalTimePickerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LocalTimePickerDialogData
  ) {}

  ngOnInit(): void {
    this.localTime24 = this.getCurrentTimeInTimezone(this.data.timezone);
    this.localDateLabel = this.formatLocalDateLabel(this.data.date);
    this.updatePreview();
  }

  onTimeChange(value: string): void {
    this.localTime24 = value;
    this.updatePreview();
  }

  onTimeBlur(): void {
    const normalizedTime = this.getNormalizedTime(this.localTime24);
    if (normalizedTime) {
      this.localTime24 = normalizedTime;
    }
    this.updatePreview();
  }

  openPicker(): void {
    if (!this.timepicker) {
      return;
    }

    this.timepicker.defaultTime =
      this.getNormalizedTime(this.localTime24) ||
      this.getCurrentTimeInTimezone(this.data.timezone);
    this.timepicker.open();
  }

  onTimePicked(value: string): void {
    const normalizedTime = this.getNormalizedTime(value);
    if (!normalizedTime) {
      return;
    }

    this.localTime24 = normalizedTime;
    this.updatePreview();
  }

  apply(): void {
    if (!this.data.date) {
      this.validationMessage = 'Select the date first.';
      return;
    }

    const normalizedTime = this.getNormalizedTime(this.localTime24);
    if (normalizedTime) {
      this.localTime24 = normalizedTime;
    }

    const utcDate = this.buildUtcDate();
    if (!utcDate) {
      this.validationMessage = 'Enter a valid time in 24-hour format.';
      return;
    }

    this.dialogRef.close(this.localTime24);
  }

  private updatePreview(): void {
    if (!this.data.date) {
      this.utcPreview = '';
      this.validationMessage = 'Select the date first.';
      return;
    }

    if (!this.localTime24 || !this.localTime24.trim()) {
      this.utcPreview = '';
      this.validationMessage = '';
      return;
    }

    const normalizedTime = this.getNormalizedTime(this.localTime24);
    const utcDate = this.buildUtcDate();
    if (!utcDate) {
      this.utcPreview = '';
      this.validationMessage = 'Enter a valid time in 24-hour format.';
      return;
    }

    this.validationMessage = '';
    this.utcPreview = this.formatUtcPreview(utcDate);
  }

  private buildUtcDate(): Date | null {
    const normalizedTime = this.getNormalizedTime(this.localTime24);
    if (!normalizedTime || !this.data.date) {
      return null;
    }

    const dateParts = this.getDateParts(this.data.date);
    if (!dateParts) {
      return null;
    }

    const [hours, minutes] = normalizedTime.split(':').map((value) =>
      Number(value)
    );
    const utcGuess = new Date(
      Date.UTC(dateParts.year, dateParts.month, dateParts.day, hours, minutes, 0)
    );
    const zonedParts = this.getZonedParts(utcGuess, this.data.timezone);
    if (!zonedParts) {
      return null;
    }

    const zonedAsUtc = Date.UTC(
      zonedParts.year,
      zonedParts.month - 1,
      zonedParts.day,
      zonedParts.hour,
      zonedParts.minute,
      zonedParts.second
    );
    const targetAsUtc = Date.UTC(
      dateParts.year,
      dateParts.month,
      dateParts.day,
      hours,
      minutes,
      0
    );

    return new Date(targetAsUtc - (zonedAsUtc - utcGuess.getTime()));
  }

  private getCurrentTimeInTimezone(timezone: string): string {
    try {
      return new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone: timezone,
      }).format(new Date());
    } catch {
      return new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).format(new Date());
    }
  }

  private formatLocalDateLabel(dateValue?: Date | string | null): string {
    const date = dateValue ? new Date(dateValue) : null;
    if (!date || Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }

  private formatUtcPreview(date: Date): string {
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone: 'UTC',
    }).format(date) + ' UTC';
  }

  private getDateParts(
    dateValue: Date | string
  ): { year: number; month: number; day: number } | null {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return {
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate(),
    };
  }

  private getZonedParts(
    date: Date,
    timezone: string
  ):
    | {
        year: number;
        month: number;
        day: number;
        hour: number;
        minute: number;
        second: number;
      }
    | null {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      });
      const parts = formatter.formatToParts(date);
      const values = Object.fromEntries(
        parts
          .filter((part) => part.type !== 'literal')
          .map((part) => [part.type, Number(part.value)])
      );

      return {
        year: values['year'],
        month: values['month'],
        day: values['day'],
        hour: values['hour'],
        minute: values['minute'],
        second: values['second'],
      };
    } catch {
      return null;
    }
  }

  private isValidTime(value: string): boolean {
    return !!this.getNormalizedTime(value);
  }

  private getNormalizedTime(value: string): string | null {
    if (!value || typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();
    const match = trimmedValue.match(/^([0-1]?\d|2[0-3]):([0-5]\d)$/);
    if (!match) {
      return null;
    }

    return `${match[1].padStart(2, '0')}:${match[2]}`;
  }

}